<!-- biome-ignore-all lint/correctness/useVueVForKey: Init Biome -->

<script setup lang="ts">
import { computed, ref, watch } from "vue";

import { useSlotValidation } from "@/composables/useSlotValidation";

import type { DropdownDirections, DropdownItemProps, supporttedEventNames } from "@/dsComponents/Dropdown/types";

import ControlledDropdown from "@/dsComponents/Dropdown/ControlledDropdown.vue";
import DropdownItem from "@/dsComponents/Dropdown/DropdownItem.vue";
import TextInput from "@/dsComponents/inputs/TextInput.vue";
import TextInputAlt from "@/dsComponents/inputs/TextInputAlt.vue";

interface SelectOption extends DropdownItemProps {
    value?: string | number;
}

interface SelectProps {
    label: string;
    modelValue?: string;
    placeholder?: string;
    description?: string | boolean;
    options: SelectOption[];
    disabled?: boolean;
    width?: string;
    size?: "xs" | "sm" | "md" | "lg";
    maxHeight?: string;
    filter?: boolean;
    iconLeft?: string;
    highlight?: boolean;
    enableLabel?: boolean;
    required?: boolean;
    error?: string;
    alt?: boolean;
    variant?: "ghost" | "stroke" | "transparent" | "grey" | "grey2";
    focused?: boolean;
    fitContent?: boolean;
    name?: string;
    direction?: DropdownDirections;
}

const props = withDefaults(defineProps<SelectProps>(), {
    size: "md",
    width: "300px",
    maxHeight: "500px",
    filter: true,
    iconLeft: "search",
    highlight: true,
    focused: undefined,
});

const emit = defineEmits<{
    (event: "update:modelValue", value: string): void;
    (event: "select", option: SelectOption | string): void;
    (event: "more"): void;
}>();
const inputFocused = ref(props.focused ?? false);
const slots = useSlotValidation("optionfooter");

const handleChange = (value: string) => {
    emit("update:modelValue", value);
};

const filteredOptions = computed<SelectOption[]>(() => {
    if (!props.modelValue || !props.filter) {
        return props.options;
    }
    const highlightRegex = new RegExp(props.modelValue, "i");
    return props.options.filter((option) => highlightRegex.test(String(option.label)));
});

watch(
    () => props.focused,
    (value: boolean | undefined) => {
        inputFocused.value = value ?? false;
    }
);

const handleSelect = (option: SelectOption) => {
    inputFocused.value = false;
    emit("select", option);
};

const handleReset = () => {
    emit("select", "");
    emit("update:modelValue", "");
};

const handleDropdownChange = (event: supporttedEventNames) => {
    if (event === "clickaway") inputFocused.value = false;
};

// Infinit scroll implementation
const companyContainer = ref();
const moreEmitted = ref(false);
const handleScroll = (event: Event) => {
    if (!companyContainer.value) return;
    const scrollTop = (event.target as HTMLElement).scrollTop + (companyContainer.value as HTMLElement).clientHeight;
    const percent = Math.round((100 * scrollTop) / (companyContainer.value as HTMLElement).scrollHeight);
    if (percent >= 90 && !moreEmitted.value) {
        emit("more");
        moreEmitted.value = true;
    }
};

watch(filteredOptions, () => {
    moreEmitted.value = false;
});
</script>
<template>
    <div class="ds-select">
        <ControlledDropdown
            :open="filteredOptions.length > 0 && inputFocused"
            @change="handleDropdownChange"
            :max-height="maxHeight"
            :fit-content="fitContent"
            :direction="direction"
            :width="width"
        >
            <template #trigger-zone>
                <TextInputAlt
                    v-if="alt"
                    :disabled="disabled"
                    :modelValue="modelValue ?? ''"
                    :width="width"
                    :size="size"
                    :placeholder="placeholder"
                    :icon-left="iconLeft"
                    :error="error"
                    :variant="variant"
                    :icon-right="modelValue && 'x'"
                    @update:model-value="handleChange"
                    @focus="inputFocused = true"
                    @right-icon-click="handleReset"
                    :validate="false"
                    :focused="focused"
                    :name="name"
                />
                <TextInput
                    v-else
                    :label="label"
                    :disabled="disabled"
                    :modelValue="modelValue ?? ''"
                    :width="width"
                    :size="size"
                    :placeholder="placeholder"
                    :icon-left="iconLeft"
                    :description="description"
                    :enableLabel="enableLabel"
                    :required="required"
                    :error="error"
                    :icon-right="modelValue && 'x'"
                    @update:modelValue="handleChange"
                    @focus="inputFocused = true"
                    @right-icon-click="handleReset"
                    :validate="false"
                    :focused="focused"
                    :name="name ?? label"
                />
            </template>
            <template #dropdown-content>
                <slot name="option-header"></slot>
                <div class="option-container" ref="companyContainer" @scroll="handleScroll">
                    <slot>
                        <DropdownItem
                            v-for="option of filteredOptions"
                            v-bind="option"
                            :highlight="highlight ? modelValue : ''"
                            @click="() => handleSelect(option)"
                        />
                    </slot>
                </div>
                <div v-if="slots.optionfooter" class="option-footer">
                    <slot name="option-footer" :resultCount="filteredOptions?.length ?? 0"></slot>
                </div>
            </template>
        </ControlledDropdown>
    </div>
</template>
<style lang="scss">
@mixin inputShadow($color) {
    box-shadow: 0 0 0 3px $color;
}

$transition-time: 0.2s;

.option-container {
    display: flex;
    flex-direction: column;
    gap: 4px;
    height: calc(100% - 30px);
    overflow-y: auto;
}

:deep(.dropdown-content) {
    .option-footer {
        padding: 8px;
    }
}
</style>
